import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, PiggyBank } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { EXPENSE_CATEGORIES, CURRENCIES, fmtMoney, budgetColor } from "@/lib/expenseConstants";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const BudgetsTab = ({ workspaceId, projects }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [cats, setCats] = useState([]);
  const [projectId, setProjectId] = useState("");

  const { data: budgets = [] } = useQuery({
    queryKey: ["budgets", workspaceId],
    queryFn: async () => (await api.get(`/workspaces/${workspaceId}/budgets`)).data,
  });

  const toggleCat = (c) => setCats((s) => (s.includes(c) ? s.filter((x) => x !== c) : [...s, c]));

  const create = async () => {
    if (!name.trim()) return toast.error("El nombre es obligatorio");
    const amt = parseFloat(target);
    if (!amt || amt <= 0) return toast.error("Importe objetivo inválido");
    try {
      await api.post(`/workspaces/${workspaceId}/budgets`, {
        name, target_amount: amt, currency,
        start_date: startDate || null, end_date: endDate || null,
        categories: cats, project_id: projectId || null,
      });
      qc.invalidateQueries({ queryKey: ["budgets", workspaceId] });
      toast.success("Presupuesto creado");
      setOpen(false);
      setName(""); setTarget(""); setCats([]); setStartDate(""); setEndDate(""); setProjectId("");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (b) => {
    try {
      await api.delete(`/budgets/${b.id}`);
      qc.invalidateQueries({ queryKey: ["budgets", workspaceId] });
      toast.success("Presupuesto eliminado");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="budgets-tab">
      <div className="flex justify-end">
        <Button size="sm" data-testid="budget-create-btn" onClick={() => setOpen(true)}>
          <Plus className="h-4 w-4 mr-1" /> Nuevo presupuesto
        </Button>
      </div>
      {budgets.length === 0 ? (
        <Card className="p-10 text-center border-dashed">
          <PiggyBank className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">Crea un presupuesto y controla el gasto previsto frente al real.</p>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {budgets.map((b) => {
            const color = budgetColor(b.pct);
            return (
              <Card key={b.id} className="p-5 space-y-3 group" data-testid={`budget-card-${b.id}`}>
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-semibold truncate">{b.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {b.project_name ? `${b.project_icon} ${b.project_name}` : "Todo el Espacio"}
                      {b.start_date && ` · ${b.start_date} → ${b.end_date || "∞"}`}
                    </p>
                  </div>
                  <span className="font-heading font-bold text-lg" style={{ color }} data-testid={`budget-pct-${b.id}`}>
                    {b.pct}%
                  </span>
                  <button onClick={() => remove(b)} data-testid={`budget-delete-btn-${b.id}`}
                    className="opacity-0 group-hover:opacity-100 text-destructive transition-opacity">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="h-2.5 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full rounded-full transition-all"
                    style={{ width: `${Math.min(b.pct, 100)}%`, backgroundColor: color }} />
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Gastado: <b className="text-foreground">{fmtMoney(b.spent, b.currency)}</b></span>
                  <span className="text-muted-foreground">Disponible: <b style={{ color: b.available < 0 ? "#D93025" : undefined }} className="text-foreground">{fmtMoney(b.available, b.currency)}</b></span>
                  <span className="text-muted-foreground">Objetivo: <b className="text-foreground">{fmtMoney(b.target_amount, b.currency)}</b></span>
                </div>
                {b.categories?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {b.categories.map((c) => {
                      const cat = EXPENSE_CATEGORIES.find((x) => x.value === c);
                      return <span key={c} className="text-[10px] bg-secondary rounded-full px-2 py-0.5">{cat?.emoji} {cat?.label}</span>;
                    })}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto" data-testid="budget-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Nuevo presupuesto</DialogTitle>
            <DialogDescription>Para el Espacio, un proyecto o unas categorías concretas.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-[1fr,110px] gap-2">
              <div className="space-y-1.5">
                <Label>Nombre</Label>
                <Input data-testid="budget-name-input" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Vacaciones Japón" maxLength={100} />
              </div>
              <div className="space-y-1.5">
                <Label>Objetivo</Label>
                <Input data-testid="budget-target-input" type="number" min="0" step="0.01" value={target}
                  onChange={(e) => setTarget(e.target.value)} placeholder="3000" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Moneda</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger data-testid="budget-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Inicio</Label>
                <Input type="date" data-testid="budget-start-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Fin</Label>
                <Input type="date" data-testid="budget-end-input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Proyecto (opcional)</Label>
              <Select value={projectId || "__none__"} onValueChange={(v) => setProjectId(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="budget-project-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Todo el Espacio</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.icon} {p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Categorías incluidas (vacío = todas)</Label>
              <div className="flex flex-wrap gap-1.5">
                {EXPENSE_CATEGORIES.map((c) => (
                  <button key={c.value} onClick={() => toggleCat(c.value)} data-testid={`budget-cat-${c.value}`}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${cats.includes(c.value) ? "bg-primary text-primary-foreground border-primary" : "hover:bg-secondary"}`}>
                    {c.emoji} {c.label}
                  </button>
                ))}
              </div>
            </div>
            <Button className="w-full" data-testid="budget-submit-btn" onClick={create}>Crear presupuesto</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
